import { ArrowForwardRounded, CloseRounded } from '@mui/icons-material'
import { alpha, Box, Button, Card, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, IconButton, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import API_URLS from '../../backend/ApiUrls'
import { ApprovalEntityType, IApprovalAuditChanges, IApprovalRequest, statusOptions } from '../../definitions/Approval'
import { INode } from '../../definitions/Nodes'
import Formatter from '../../helpers/Formatter'
import FlowUIManager from '../../managers/FlowUIManager'
import { useAppDispatch, useAppSelector } from '../../store/Hooks'
import { changeRoute } from '../../store/slices/SessionSlice'
import JSONDiff from '../editor/JSONDiff'
import NodesIcon from '../flows/nodes/NodesIcon'
import useCommand from '../hooks/useCommand'
import useInvalidate from '../hooks/useInvalidate'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import RoutingManager from '../routing/Routing'
import { snackbar } from '../services/SnackbarService'
import ApprovalModal from './ApprovalHubModal'
import ApprovalUsersModal from './ApprovalsUsersModal'

const statusChip = (status: string, isHead: boolean) => (
    <Chip
        label={(
            <Box display='flex' alignItems='center' gap={1}>
                <Box
                    width={8}
                    height={8}
                    borderRadius='50%'
                    bgcolor={
                        (status === 'approved' && isHead)
                            ? 'success.main'
                            : (status === 'approved' && !isHead)
                                    ? 'grey.500'
                                    : status === 'ready-for-review'
                                        ? 'info.main'
                                        : status === 'rejected'
                                            ? 'red'
                                            : 'orange'
                    }
                />
                <Typography variant='body2'>
                    {statusOptions.find(option => option.value === status).label}
                </Typography>
            </Box>
        )}
        variant='outlined'
        size='small'
    />
)

const extractValue = (val: any) => {
    if (val === '') return '""'
    if (val === undefined || val === null) return String(val)
    if (typeof val === 'object' || Array.isArray(val)) return <pre>{JSON.stringify(val, null, 2)}</pre>
    return String(val)
}

const renderNodeUI = (node: INode) => {
    const myColor = FlowUIManager.getNodeColor(node?.type)

    return (
        <Card
            sx={{
                height: 60,
                flex: 1,
                padding: 1,
                borderLeft: !node || node.type === 'start' ? undefined : `var(--unit) solid ${myColor}`,
                borderTopLeftRadius: 'calc(var(--unit) - 1px)',
                borderBottomLeftRadius: 'calc(var(--unit) - 1px)',
                backgroundColor: theme => !node ? alpha(theme.palette.action.disabled, 0.001) : undefined
            }}>
            {node && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, padding: 1 }}>
                    <NodesIcon nodeType={node.type} size={25} />
                    <Typography sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{node.name}</Typography>
                </Box>
            )}
        </Card>
    )
}

const renderRow = (comparison: IApprovalAuditChanges, onDetailsClick: (comparison: IApprovalAuditChanges) => void) => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Box flex={1} display='flex'>
                {renderNodeUI(comparison?.prevValue)}
            </Box>
            <Box gap={2} flexDirection='row' flex={1.5} display='flex' alignItems='center'>
                <Divider sx={{ flex: 1, borderBottomWidth: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    <Button
                        variant='text'
                        size='small'
                        color='secondary'
                        onClick={() => onDetailsClick(comparison)}>
                        Details
                    </Button>
                </Box>
                <Divider sx={{ flex: 1, borderBottomWidth: 2 }} />
            </Box>
            <Box flex={1} display='flex'>
                {renderNodeUI(comparison?.newValue)}
            </Box>
        </Box>
    )
}

const ApprovalAudit = () => {
    const params = RoutingManager.getParams<{ entityName: string, entityType: ApprovalEntityType, approvalEntityUUID: string, originalEntityUUID: string, approvalType: 'user' | 'admin' }>(useAppSelector(s => s.session.routeParams))
    const [openNodeConfigComparison, setOpenNodeConfigComparison] = useState(false)
    const [openApprovalModal, setOpenApprovalModal] = useState(false)
    const [selectedApproval, setSelectedApproval] = useState<IApprovalRequest>(null)
    const [selectedComparison, setSelectedComparison] = useState<IApprovalAuditChanges | null>(null)
    const dispatch = useAppDispatch()

    const invalidateContractListSmartTable = useInvalidate({ queryKey: buildQueryKey(API_URLS.os.getContracts + '/*') })
    const invalidateContractSchemaList = useInvalidate({ queryKey: buildQueryKey(API_URLS.os.getAllContractSchemas + '/*') })
    const invalidateSchemaVersions = useInvalidate({ queryKey: buildQueryKey('schema-versions/*') })
    const invalidateApprovalHub = useInvalidate({ queryKey: buildQueryKey('get-approvals') })
    const invalidateSchemaData = useInvalidate({ queryKey: buildQueryKey('get-schema/*') })
    const invalidateEntityApprovalStatus = useInvalidate({ queryKey: buildQueryKey('entity-approval/*') })
    const invalidateContractData = useInvalidate({ queryKey: buildQueryKey('get-contract/*') })
    const invalidateContractsVersion = useInvalidate({ queryKey: buildQueryKey('contract-versions/*') })
    const invalidateFlowSettlementApprovals = useInvalidate({ queryKey: buildQueryKey(`get-approval-/*`) })
    const invalidateFlow = useInvalidate({ queryKey: buildQueryKey(`get-flow-/*`) })
    const invalidateFlowVersions = useInvalidate({ queryKey: buildQueryKey(`get-all-version-flows-/*`) })
    const invalidateSettlementDetails = useInvalidate({ queryKey: buildQueryKey('settlement-details' + '/*') })
    const invalidateSettlements = useInvalidate({ queryKey: buildQueryKey('settlements' + '/*') })

    const { status: auditStatus, response: auditResponse } = useQuery({
        queryKey: buildQueryKey('get-approval-audit'),
        queryFn: () => ApiClient.getEntitiesAudit(params.entityType, params.approvalEntityUUID, params.originalEntityUUID),
        cache: false
    })

    const { execute: executeApprove, status: approveStatus } = useCommand({
        commandFn: async (approvalEntityUUID: string) => {
            return await ApiClient.approveRequest(approvalEntityUUID)
        },
        onFinish: result => {
            if (result.status === 'success' && result.response) {
                snackbar.show({
                    severity: 'success',
                    text: `Approval granted for ${selectedApproval?.entityName}`
                })

                invalidateApprovalHub()
                invalidateEntityApprovalStatus()

                if (selectedApproval?.entityType === 'contract') {
                    invalidateContractListSmartTable()
                    invalidateContractData()
                    invalidateContractsVersion()
                }
                if (selectedApproval?.entityType === 'schema') {
                    invalidateContractSchemaList()
                    invalidateSchemaData()
                    invalidateSchemaVersions()
                }
                if (selectedApproval?.entityType === 'flow') {
                    invalidateFlowSettlementApprovals()
                    invalidateFlowVersions()
                    invalidateFlow()
                }
                if (selectedApproval?.entityType === 'settlement') {
                    invalidateFlowSettlementApprovals()
                    invalidateSettlementDetails()
                    invalidateSettlements()
                }

                dispatch(
                    changeRoute({
                        route: 'approvals'
                    })
                )
                handleCloseApprovalModal()
            } else if (result.status === 'error') {
                console.error('Error approving request:', result.errorText)
                snackbar.show({
                    severity: 'error',
                    text: 'Failed to approve request: ' + result.errorText
                })
            }
        }
    })

    const { execute: executeReject, status: rejectStatus } = useCommand({
        commandFn: async (data: { approvalEntityUUID: string, rejectionDescription: string }) => {
            return await ApiClient.rejectApproval(
                data.approvalEntityUUID,
                data.rejectionDescription
            )
        },
        onFinish: result => {
            if (result.status === 'success' && result.response) {
                snackbar.show({
                    severity: 'info',
                    text: `Approval rejected for ${selectedApproval?.entityName}`
                })

                invalidateApprovalHub()
                invalidateEntityApprovalStatus()

                if (selectedApproval.entityType === 'contract') {
                    invalidateContractListSmartTable()
                    invalidateContractData()
                    invalidateContractsVersion()
                }

                if (selectedApproval.entityType === 'schema') {
                    invalidateContractSchemaList()
                    invalidateSchemaData()
                    invalidateSchemaVersions()
                }

                if (selectedApproval.entityType === 'flow') {
                    invalidateFlowSettlementApprovals()
                    invalidateFlowVersions()
                    invalidateFlow()
                }

                if (selectedApproval.entityType === 'settlement') {
                    invalidateFlowSettlementApprovals()
                    invalidateSettlementDetails()
                    invalidateSettlements()
                }

                dispatch(
                    changeRoute({
                        route: 'approvals'
                    })
                )
                handleCloseApprovalModal()
            } else if (result.status === 'error') {
                console.error('Error rejecting request:', result.errorText)
                snackbar.show({
                    severity: 'error',
                    text: 'Failed to approve request: ' + result.errorText
                })
            }
        }
    })

    const { execute: navigateToContract, status: navigateToContractStatus } = useCommand({
        commandFn: async (item: IApprovalRequest) => {
            const contract = await ApiClient.getContract(item.approvalEntityUUID)
            const contractFiles = await ApiClient.getContractFiles(item.approvalEntityUUID)

            return { contract, contractFiles }
        },
        onFinish: result => {
            if (result.status === 'success') {
                dispatch(
                    changeRoute({
                        route: 'contract-editor',
                        params: {
                            contractUUID: result.response.contract.uuid,
                            uploadedFiles: result.response.contractFiles ?? []
                        },
                        customRouteName: result.response.contract.name
                    })
                )
            }
        }
    })

    const { execute: navigateToSettlement, status: navigateToSettlementStatus } = useCommand({
        commandFn: async (item: IApprovalRequest) => await ApiClient.getSettlementDetails(item.approvalEntityUUID),
        onFinish: result => {
            if (result.status === 'success') {
                dispatch(
                    changeRoute({
                        route: 'settlement-details',
                        params: {
                            settlementId: result.response.settlement.uuid,
                            contractId: result.response.settlement.contractId,
                            eeId: result.response.settlement.executionEnvironmentId
                        }
                    })
                )
            }
        }
    })

    const handleEditApproval = (approval: IApprovalRequest) => {
        switch (approval.entityType) {
            case 'contract':
                navigateToContract(approval)
                break
            case 'schema':
                dispatch(
                    changeRoute({
                        route: 'contract-schema-editor',
                        params: { uuid: approval.approvalEntityUUID }
                    })
                )
                break
            case 'flow':
                dispatch(
                    changeRoute({
                        route: 'flow',
                        params: { flowId: approval.approvalEntityUUID }
                    })
                )
                break
            case 'settlement':
                navigateToSettlement(approval)
                break
            default:
                console.warn(`Unknown entityType: ${approval.entityType}`)
                break
        }

        setOpenApprovalModal(false)
    }

    const handleOpenNodeConfigComparison = (comparison: IApprovalAuditChanges) => {
        setSelectedComparison(comparison)
        setOpenNodeConfigComparison(true)
    }

    const handleCloseNodeConfigComparison = () => {
        setOpenNodeConfigComparison(false)
    }

    const handleOpenApprovalModal = (item: IApprovalRequest) => {
        setSelectedApproval(item)
        setOpenApprovalModal(true)
    }

    const handleCloseApprovalModal = () => {
        setOpenApprovalModal(false)
    }

    const handleApprove = async () => {
        if (!selectedApproval) return
        await executeApprove(selectedApproval.uuid)
    }

    const handleReject = async (rejectionReason: string) => {
        if (!selectedApproval || !rejectionReason.trim()) return

        await executeReject({
            approvalEntityUUID: selectedApproval.uuid,
            rejectionDescription: rejectionReason.trim()
        })
    }

    if (auditStatus === 'loading' || auditStatus === 'stale' || navigateToContractStatus === 'loading' || navigateToSettlementStatus === 'loading') {
        return (
            <Stack sx={{ width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
            </Stack>
        )
    }

    if (auditStatus === 'error') {
        return (
            <Stack sx={{ width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }}>
                <Typography variant='body1' color={theme => theme.palette.error.main}>Something went wrong</Typography>
            </Stack>
        )
    }

    return (
        <>
            <Stack spacing={3} sx={{ width: '100%', height: '100%', overflow: 'hidden', padding: 1 }}>
                <Stack spacing={0.5}>
                    <Stack flexDirection='row' justifyContent='space-between'>
                        <Typography variant='h5' fontWeight={700} marginTop={1}>{params.entityName}</Typography>
                        <Button variant='contained' onClick={() => handleOpenApprovalModal(auditResponse.newEntity.approval)}>Manage</Button>
                    </Stack>
                    <Typography variant='body1'>View all the changes done to this file</Typography>
                </Stack>
                <Stack flexDirection='row' gap={3} alignItems='center'>
                    {auditResponse.oldEntity && (
                        <>
                            <Stack spacing={1}>
                                <Typography variant='subtitle2' fontWeight={500}>{Formatter.date(new Date(auditResponse.oldEntity.approval.changes.at(-1).changedAt), 'mm/dd/yyyy hh:mm:ss')}</Typography>
                                <Stack flexDirection='row' gap={1} alignItems='baseline'>
                                    <Typography variant='body2' color={theme => theme.palette.text.secondary}>Title</Typography>
                                    <Typography variant='body2'>{auditResponse.oldEntity.approval.entityName}</Typography>
                                </Stack>
                                <Stack flexDirection='row' gap={1} alignItems='baseline'>
                                    <Typography variant='body2' color={theme => theme.palette.text.secondary}>Version</Typography>
                                    <Typography variant='body2'>{auditResponse.oldEntity.versioning.version}</Typography>
                                </Stack>
                                <Stack flexDirection='row' gap={1} alignItems='center'>
                                    <Typography variant='body2' color={theme => theme.palette.text.secondary}>Status</Typography>
                                    {statusChip(auditResponse.oldEntity.approval.status, auditResponse.oldEntity.versioning.isHead)}
                                </Stack>
                            </Stack>
                            <Stack>
                                <ArrowForwardRounded />
                            </Stack>
                        </>
                    )}
                    <Stack spacing={1}>
                        <Typography variant='subtitle2' fontWeight={500}>{Formatter.date(new Date(auditResponse.newEntity.approval.changes.at(-1).changedAt), 'mm/dd/yyyy hh:mm:ss')}</Typography>
                        <Stack flexDirection='row' gap={1} alignItems='baseline'>
                            <Typography variant='body2' color={theme => theme.palette.text.secondary}>Title</Typography>
                            <Typography variant='body2'>{auditResponse.newEntity.approval.entityName}</Typography>
                        </Stack>
                        <Stack flexDirection='row' gap={1} alignItems='baseline'>
                            <Typography variant='body2' color={theme => theme.palette.text.secondary}>Version</Typography>
                            <Typography variant='body2'>{auditResponse.newEntity.versioning.version}</Typography>
                        </Stack>
                        <Stack flexDirection='row' gap={1} alignItems='center'>
                            <Typography variant='body2' color={theme => theme.palette.text.secondary}>Status</Typography>
                            {statusChip(auditResponse.newEntity.approval.status, auditResponse.newEntity.versioning.isHead)}
                        </Stack>
                    </Stack>
                </Stack>
                <Card sx={{ padding: 3, overflowY: 'auto' }}>
                    <Stack spacing={2} flex={1}>
                        {auditResponse.comparisons.length === 0 && <Typography variant='body1'>No changes available, the entities are identical.</Typography>}
                        {auditResponse.comparisons.map((c, i) => {
                            if (params.entityType === 'flow')
                                return (
                                    <Stack key={i} spacing={1}>
                                        {renderRow(c, handleOpenNodeConfigComparison)}
                                    </Stack>
                                )
                            return (
                                <Stack key={i} spacing={1}>
                                    <Stack flexDirection='row' gap={0.5} alignItems='baseline'>
                                        <Typography variant='body2'>{c.type}</Typography>
                                        <Typography variant='body2' fontWeight={500}>{c.path}</Typography>
                                    </Stack>
                                    <Stack flexDirection='row' gap={0.5} alignItems='center'>
                                        {(c.type === 'update' || c.type === 'delete') && (
                                            <Stack
                                                sx={{
                                                    padding: 'calc(var(--unit) * 0.5) var(--unit)',
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    borderColor: theme => alpha(theme.palette.error.main, 0.5),
                                                    backgroundColor: theme => alpha(theme.palette.error.main, 0.3) }}>
                                                <Typography variant='caption'>{extractValue(c.prevValue)}</Typography>
                                            </Stack>
                                        )}
                                        {c.type === 'update' && <ArrowForwardRounded fontSize='small' />}
                                        {(c.type === 'update' || c.type === 'create') && (
                                            <Stack
                                                sx={{
                                                    padding: 'calc(var(--unit) * 0.5) var(--unit)',
                                                    borderRadius: 1, border: '1px solid',
                                                    borderColor: theme => alpha(theme.palette.success.main, 0.5),
                                                    backgroundColor: theme => alpha(theme.palette.success.main, 0.3) }}>
                                                <Typography variant='caption'>{extractValue(c.newValue)}</Typography>
                                            </Stack>
                                        )}
                                    </Stack>
                                </Stack>
                            )
                        })}
                    </Stack>
                </Card>
            </Stack>
            <Dialog open={openNodeConfigComparison} onClose={handleCloseNodeConfigComparison} PaperProps={{ sx: { maxWidth: 800, maxHeight: 800, display: 'flex', flex: 1 } }}>
                <DialogTitle>
                    <Stack flexDirection='row' alignItems='center' justifyContent='space-between'>
                        <Typography variant='h6' fontWeight={500}>Details for node configuration</Typography>
                        <IconButton size='small' onClick={handleCloseNodeConfigComparison}>
                            <CloseRounded fontSize='small' />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <JSONDiff
                        currentValue={selectedComparison?.newValue?.data ?? ''}
                        comparedValue={selectedComparison?.prevValue?.data ?? ''}
                        order='a-b'
                    />
                </DialogContent>
            </Dialog>
            {params.approvalType === 'admin'
                ? (
                        <ApprovalModal
                            open={openApprovalModal}
                            onClose={handleCloseApprovalModal}
                            approval={selectedApproval}
                            onApprove={handleApprove}
                            onReject={handleReject}
                            rejectStatus={rejectStatus}
                            approveStatus={approveStatus}
                        />
                    )
                : params.approvalType === 'user'
                    ? (
                            <ApprovalUsersModal
                                open={openApprovalModal}
                                onClose={handleCloseApprovalModal}
                                approval={selectedApproval}
                                onEdit={handleEditApproval}
                            />
                        )
                    : null}
        </>
    )
}

export default ApprovalAudit
